import { useCallback, useEffect, useMemo, useState } from 'react'
import { MENU_NAMES } from 'texts/menuNames'
import { PROFILE_LIST } from 'texts/profileList'
import { Container } from 'ui/molecules/Container'
import { DaysPicker, Typography } from '@frontend/design_system'
import { ApplicationList } from '../ApplicationList'
import {
  APPLICATION_SEARCH_PARAM,
  APPLICATION_SHOWN_STATUS,
} from 'modules/Applications/constants'
import styles from './styles.module.scss'
import { useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { STOP_SALES_DATE_FORMAT } from 'constants/date'
import { applicationsSelectedRangeSelector } from 'redux/applications/selectors'
import { useApiRequest } from 'hooks/useApiRequest'
import { applicationsApi } from 'api'
import { StatusCountersResponse } from 'api/applications/types'
import { SP_STATUS } from 'constants/searchParams'
import { ApplicationStatusSearchParam } from '../ApplicationList/types'
import { formatStatus } from './utils'
import {
  selectedFiltersCountSelector,
  selectedFiltersSelector,
} from 'redux/filters/selectors'
import { Filter } from 'utils/addQueryString'
import { prepareFiltersForReq } from 'utils/table'
import { REVERSE_COR_MAP } from '../ApplicationList/constants'
import { setApplicationsSelectedRange } from 'redux/applications/slice'
import { NavigationItem } from 'ui/atoms/NavigationItem/NavigationItem'

export const AllApplications = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialActiveKey =
    (searchParams.get(SP_STATUS) as ApplicationStatusSearchParam) ||
    APPLICATION_SEARCH_PARAM.WaitingList

  const [activeKey, setActiveKey] =
    useState<ApplicationStatusSearchParam>(initialActiveKey)
  const selectedRange = useSelector(applicationsSelectedRangeSelector)
  const selectedFiltersCount = useSelector(selectedFiltersCountSelector)
  const selectedFilters = useSelector(selectedFiltersSelector)
  const dispatch = useDispatch()
  const [statusCounter, setStatusCounter] = useState<StatusCountersResponse>([])

  const statusCountersRequest = useApiRequest((fromDate, toDate, filters) =>
    applicationsApi.getStatusCounter(fromDate, toDate, filters)
  )

  useEffect(() => {
    if (!searchParams.get(SP_STATUS)) {
      setSearchParams({ [SP_STATUS]: APPLICATION_SEARCH_PARAM.WaitingList })
    }
  }, [])

  useEffect(() => {
    if (selectedRange.from && selectedRange.to) {
      const fetchCounters = async () => {
        let filters: Filter[] = []

        if (selectedFiltersCount) {
          filters = prepareFiltersForReq(
            {
              ...selectedFilters.applications,
              countries: selectedFilters.applications.countries?.map(
                (name) => REVERSE_COR_MAP.get(name) || ''
              ),
            },
            selectedFiltersCount.applications,
            'applications'
          )
        }

        const statusesResponse = await statusCountersRequest.apiRequest(
          selectedRange.from,
          selectedRange.to,
          filters && filters
        )

        if (statusesResponse) {
          setStatusCounter(statusesResponse.data.statuses)
        }
      }
      fetchCounters()
    }
  }, [selectedRange, selectedFiltersCount])

  const handleClick = (activeKey: ApplicationStatusSearchParam) => {
    setActiveKey(activeKey)
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams)
      newParams.set(SP_STATUS, activeKey)
      return newParams
    })
  }

  useEffect(() => {
    const currentActiveKey = searchParams.get(
      SP_STATUS
    ) as ApplicationStatusSearchParam
    if (currentActiveKey) {
      setActiveKey(currentActiveKey)
    }
  }, [searchParams])

  const handleSelectedRange = useCallback(
    (newValues: { from: Date; to: Date }) => {
      dispatch(
        setApplicationsSelectedRange({
          from: format(newValues.from, STOP_SALES_DATE_FORMAT),
          to: newValues.to ? format(newValues.to, STOP_SALES_DATE_FORMAT) : '',
        })
      )
    },
    []
  )

  const daysPickerRange = useMemo(
    () => ({
      from: selectedRange.from ? new Date(selectedRange.from) : null,
      to: selectedRange.to ? new Date(selectedRange.to) : null,
    }),
    [selectedRange]
  )

  return (
    <Container
      title={MENU_NAMES.APPLICATIONS}
      rightBlock={
        <div className={styles['date-picker']}>
          <DaysPicker
            range={daysPickerRange}
            initialFrom={selectedRange.from}
            initialTo={selectedRange.to}
            size="small"
            handleRangeSelect={handleSelectedRange}
            variant="secondary"
          />
        </div>
      }
      tooltip={PROFILE_LIST.TOOLTIP_CONTENT}
      withCompanyName={false}
      loading={statusCountersRequest.loading && !statusCountersRequest.isLoaded}
    >
      <>
        <div className={styles.navigation}>
          {Object.entries(APPLICATION_SHOWN_STATUS).map(([key, value], i) => (
            <NavigationItem
              label={
                <>
                  {value}{' '}
                  <Typography
                    color="blue700"
                    name="caption2WBold"
                    className={styles.counter}
                  >
                    {statusCounter[i]?.count}
                  </Typography>
                </>
              }
              onClick={() => handleClick(key as ApplicationStatusSearchParam)}
              isActive={key === activeKey}
              id={key}
              key={key}
            />
          ))}
        </div>
        <ApplicationList
          activeKey={activeKey}
          status={formatStatus(activeKey as ApplicationStatusSearchParam)}
          selectedRange={selectedRange}
        />
      </>
    </Container>
  )
}
